import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ToggleSwitch } from "../adminSurface/AdminSurface";
import {
  getDeliveryBranding, saveDeliveryBranding, previewDeliveryBranding,
} from "../../api/adminApi";

/**
 * PORTAL BRANDING — a corporation's own name, mark and colours on the self-service portal.
 *
 * In its own file rather than as a sixth section of DeliveryPage: this tab owns its data — it loads and
 * saves a row nothing else on the page touches — and it is the only screen here that holds a file.
 *
 * <b>The preview is resolved by the server, not by this component.</b> Contrast-derived text colours and
 * the pale brand tints are computed once, in the same code path the portal paints with, and this screen
 * asks for them on a debounce while the operator adjusts. Deriving them here as well would be a second
 * implementation of one rule, and the day the two disagreed the preview would be confidently showing a
 * portal that does not exist.
 *
 * <b>Nothing here can put markup on the portal.</b> There is no stylesheet field and no HTML field, by
 * design: every input below is a value, validated again on the way in and again before it is painted, and
 * the page it lands on runs under a content-security policy that would refuse anything else.
 */

const PREVIEW_DEBOUNCE_MS = 300;

/** The neutral portal, for when nothing is branded. Mirrors the defaults in portal.css. */
const NEUTRAL = {
  accent: "#4338ca", accentInk: "#ffffff", accentSoft: "#eef2ff", accentLine: "#dcd9fb",
  header: "#ffffff", headerInk: "#0f172a", page: "#f6f7fb",
};

const EMPTY_FORM = {
  isActive: true, brandName: "", accentColor: "", headerColor: "", pageColor: "",
  footerText: "", supportUrl: "", privacyUrl: "",
};

function formOf(dto) {
  return {
    isActive: dto?.isActive !== false,
    brandName: dto?.brandName || "",
    accentColor: dto?.accentColor || "",
    headerColor: dto?.headerColor || "",
    pageColor: dto?.pageColor || "",
    footerText: dto?.footerText || "",
    supportUrl: dto?.supportUrl || "",
    privacyUrl: dto?.privacyUrl || "",
  };
}

export default function BrandingTab({ t, busy, token, organization, onError, onNotice }) {
  const [dto, setDto] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [rejected, setRejected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // A logo the operator picked but has not saved, and an explicit removal. Held apart from `form` because
  // neither is a value: "leave the stored one alone" is the third state, and it has to survive every other
  // edit on the screen.
  const [logoDraft, setLogoDraft] = useState(null);
  const [logoCleared, setLogoCleared] = useState(false);
  const [logoError, setLogoError] = useState("");
  const fileRef = useRef(null);

  const maxLogoBytes = dto?.maxLogoBytes || 512 * 1024;
  const maxLogoKb = Math.round(maxLogoBytes / 1024);
  const allowedTypes = dto?.allowedLogoTypes || ["image/png", "image/jpeg", "image/gif", "image/webp"];

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await getDeliveryBranding(token, organization || undefined);
      setDto(result);
      setForm(formOf(result));
      setPreview(result?.preview || null);
      setLogoDraft(null);
      setLogoCleared(false);
      setLogoError("");
    } catch (e) {
      onError?.(e);
    } finally {
      setLoading(false);
    }
  }, [token, organization, onError]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const dirty = useMemo(() => {
    if (loading) return false;
    if (logoDraft || logoCleared) return true;
    const saved = formOf(dto);
    return Object.keys(EMPTY_FORM).some((k) => String(form[k] ?? "") !== String(saved[k] ?? ""));
  }, [form, dto, logoDraft, logoCleared, loading]);

  /* The palette, re-resolved on a debounce while the operator adjusts — and only while the form differs
     from what is stored, since the load already came with the saved one resolved.

     A failed preview keeps the last good palette rather than blanking: a network hiccup should not make
     the screen look like the branding broke. */
  useEffect(() => {
    if (!token || loading || !dirty) return undefined;
    const timer = setTimeout(async () => {
      try {
        const result = await previewDeliveryBranding(token, { organization: organization || undefined, ...form });
        setPreview(result);
        setRejected(result?.rejected || {});
      } catch {
        /* keep the last good palette */
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [token, organization, form, loading, dirty]);

  /* The logo the preview shows: what was just picked, nothing if it was just removed, otherwise what is
     stored. Read straight from the file rather than uploaded on pick — an operator who changes their mind
     should not have left half a megabyte on the server. */
  const previewLogo = logoDraft || (logoCleared ? null : dto?.logoDataUrl) || null;

  const pickLogo = (e) => {
    const file = e.target.files && e.target.files[0];
    // Cleared immediately so choosing the SAME file again still fires a change event — otherwise a
    // re-pick after a rejected file silently does nothing.
    e.target.value = "";
    if (!file) return;

    setLogoError("");

    if (file.size > maxLogoBytes) {
      setLogoError(t("delivery.brandLogoTooBig", {
        kb: maxLogoKb, defaultValue: "That image is larger than {{kb}} KB.",
      }));
      return;
    }

    // A first pass on the browser's own idea of the type, so the common mistakes — an SVG, a PDF — are
    // answered instantly. The server decides for real, from the bytes.
    if (file.type && allowedTypes.indexOf(file.type) < 0) {
      setLogoError(t("delivery.brandLogoWrongType", {
        defaultValue: "That is not a PNG, JPEG, GIF or WebP image. SVG is not accepted.",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogoDraft(String(reader.result || ""));
      setLogoCleared(false);
    };
    reader.onerror = () => setLogoError(t("delivery.brandLogoUnreadable", {
      defaultValue: "That file could not be read.",
    }));
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoDraft(null);
    setLogoError("");
    // Only a stored logo needs clearing on the server; discarding an unsaved pick is a local act.
    setLogoCleared(!!dto?.logoDataUrl);
  };

  const save = async () => {
    if (!token) return;
    setSaving(true);
    try {
      const body = { organization: organization || undefined, ...form };
      if (logoCleared) body.clearLogo = true;
      else if (logoDraft) body.logoDataUrl = logoDraft;

      const result = await saveDeliveryBranding(token, body);
      setDto(result);
      setForm(formOf(result));
      setPreview(result?.preview || null);
      setLogoDraft(null);
      setLogoCleared(false);
      onNotice?.(t("delivery.brandSaved", { defaultValue: "Branding saved." }));
    } catch (e) {
      onError?.(e);
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    setForm(formOf(dto));
    setLogoDraft(null);
    setLogoCleared(false);
    setLogoError("");
  };

  if (loading) {
    return <div className="asf-card" style={{ color: "#64748b" }}>{t("common.loading", { defaultValue: "Loading…" })}</div>;
  }

  const disabled = busy || saving;

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 460px", minWidth: 0 }}>
        {/* Identity ─────────────────────────────────────────────────────────────────── */}
        <div className="asf-card">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>
                {t("delivery.brandIdentity", { defaultValue: "Your identity" })}
              </h3>
              <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
                {t("delivery.brandIdentityHint", {
                  defaultValue: "The portal is the only screen in this product your customers see. This is where it becomes yours.",
                })}
              </p>
            </div>
            {/* The master switch for the whole tab: off keeps everything below and shows customers the
                neutral portal, so a brand can be taken down during an incident without being lost. */}
            <ToggleSwitch
              checked={!!form.isActive}
              onChange={(v) => set("isActive", v)}
              label={t("delivery.brandApply", { defaultValue: "Apply branding" })}
            />
          </div>

          <div className="asf-grid" style={{ marginTop: 16 }}>
            <div className="asf-field" style={{ flex: "1 1 260px" }}>
              <label className="asf-lbl">{t("delivery.brandName", { defaultValue: "Name" })}</label>
              <input className="asf-input" value={form.brandName} maxLength={200}
                placeholder={dto?.inheritedName || ""}
                onChange={(e) => set("brandName", e.target.value)} />
              <p style={{ fontSize: 11.5, color: "#64748b", margin: "6px 0 0" }}>
                {dto?.inheritedName
                  ? t("delivery.brandNameInherits", {
                    name: dto.inheritedName,
                    defaultValue: "Shown in the header and the browser tab. Empty falls back to the portal heading — “{{name}}”.",
                  })
                  : t("delivery.brandNameHint", { defaultValue: "Shown in the header and the browser tab." })}
              </p>
            </div>
          </div>

          <LogoField
            t={t} disabled={disabled} logo={previewLogo} error={logoError}
            maxKb={maxLogoKb} fileRef={fileRef} onPick={pickLogo} onRemove={removeLogo}
          />
        </div>

        {/* Colours ──────────────────────────────────────────────────────────────────── */}
        <div className="asf-card">
          <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>
            {t("delivery.brandColours", { defaultValue: "Colours" })}
          </h3>
          <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px" }}>
            {t("delivery.brandColoursHint", {
              defaultValue: "Text colours are chosen for you, by contrast against what you pick — a portal cannot be configured into being unreadable.",
            })}
          </p>

          <ColorField t={t} disabled={disabled} invalid={!!rejected.accentColor}
            label={t("delivery.brandAccent", { defaultValue: "Brand colour" })}
            hint={t("delivery.brandAccentHint", { defaultValue: "Buttons, links, the selected tab and the focus ring." })}
            value={form.accentColor} fallback={NEUTRAL.accent} onChange={(v) => set("accentColor", v)} />

          <ColorField t={t} disabled={disabled} invalid={!!rejected.headerColor}
            label={t("delivery.brandHeader", { defaultValue: "Header bar" })}
            hint={t("delivery.brandHeaderHint", { defaultValue: "The bar your mark sits in. Empty keeps it white." })}
            value={form.headerColor} fallback={NEUTRAL.header} onChange={(v) => set("headerColor", v)} />

          <ColorField t={t} disabled={disabled} invalid={!!rejected.pageColor}
            label={t("delivery.brandPage", { defaultValue: "Page background" })}
            hint={t("delivery.brandPageHint", { defaultValue: "Behind the cards. Empty keeps the neutral grey." })}
            value={form.pageColor} fallback={NEUTRAL.page} onChange={(v) => set("pageColor", v)} />
        </div>

        {/* Footer ───────────────────────────────────────────────────────────────────── */}
        <div className="asf-card">
          <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>
            {t("delivery.brandFooter", { defaultValue: "Footer" })}
          </h3>
          <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px" }}>
            {t("delivery.brandFooterHint", {
              defaultValue: "Your own wording and the two links a customer-facing page is expected to carry. Shown as plain text — markup is not accepted.",
            })}
          </p>

          <div className="asf-field" style={{ marginBottom: 14 }}>
            <label className="asf-lbl">{t("delivery.brandFooterText", { defaultValue: "Footer line" })}</label>
            <textarea className="asf-input" rows={2} maxLength={400} value={form.footerText}
              placeholder={t("delivery.brandFooterPlaceholder", {
                defaultValue: "Documents are provided by the institution that sent you this link.",
              })}
              onChange={(e) => set("footerText", e.target.value)} />
          </div>

          <div className="asf-grid">
            <div className="asf-field" style={{ flex: "1 1 240px" }}>
              <label className="asf-lbl">{t("delivery.brandSupport", { defaultValue: "Help link" })}</label>
              <input className="asf-input" value={form.supportUrl} maxLength={400}
                placeholder="https://bank.example/help"
                style={rejected.supportUrl ? { borderColor: "#fca5a5" } : undefined}
                onChange={(e) => set("supportUrl", e.target.value)} />
            </div>
            <div className="asf-field" style={{ flex: "1 1 240px" }}>
              <label className="asf-lbl">{t("delivery.brandPrivacy", { defaultValue: "Privacy link" })}</label>
              <input className="asf-input" value={form.privacyUrl} maxLength={400}
                placeholder="https://bank.example/privacy"
                style={rejected.privacyUrl ? { borderColor: "#fca5a5" } : undefined}
                onChange={(e) => set("privacyUrl", e.target.value)} />
            </div>
          </div>
          {(rejected.supportUrl || rejected.privacyUrl) && (
            <p style={{ fontSize: 12, color: "#b91c1c", margin: "10px 0 0" }}>
              {t("delivery.brandLinkInvalid", {
                defaultValue: "A portal link must be an https:// address or a mailto: address.",
              })}
            </p>
          )}
        </div>

        <div className="asf-grid" style={{ alignItems: "center" }}>
          <button className="asf-cta" onClick={save} disabled={disabled || !dirty}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
          <button className="asf-btn" onClick={revert} disabled={disabled || !dirty}>
            {t("delivery.brandRevert", { defaultValue: "Discard changes" })}
          </button>
          {dirty && (
            <span style={{ fontSize: 12.5, color: "#92400e" }}>
              {t("delivery.brandUnsaved", { defaultValue: "Unsaved — customers still see the last saved version." })}
            </span>
          )}
        </div>
      </div>

      <BrandPreview t={t} palette={preview} active={!!form.isActive} logo={previewLogo}
        name={form.brandName || dto?.inheritedName || ""} />
    </div>
  );
}

/* ── Logo ──────────────────────────────────────────────────────────────────────────────
   Shown against a checkerboard, because a mark is usually transparent and a white-on-white
   logo that looks blank here is a support call. */
function LogoField({ t, disabled, logo, error, maxKb, fileRef, onPick, onRemove }) {
  return (
    <div style={{ marginTop: 18 }}>
      <label className="asf-lbl">{t("delivery.brandLogo", { defaultValue: "Logo" })}</label>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{
          width: 148, height: 56, borderRadius: 12, border: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 8,
          backgroundColor: "#fff",
          backgroundImage: "linear-gradient(45deg,#f1f5f9 25%,transparent 25%,transparent 75%,#f1f5f9 75%)," +
            "linear-gradient(45deg,#f1f5f9 25%,transparent 25%,transparent 75%,#f1f5f9 75%)",
          backgroundSize: "12px 12px", backgroundPosition: "0 0, 6px 6px",
        }}>
          {logo
            ? <img src={logo} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            : <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{t("delivery.brandNoLogo", { defaultValue: "No logo" })}</span>}
        </div>

        <div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
            style={{ display: "none" }} onChange={onPick} />
          <button className="asf-btn" disabled={disabled} onClick={() => fileRef.current?.click()}>
            {logo
              ? t("delivery.brandLogoReplace", { defaultValue: "Replace" })
              : t("delivery.brandLogoChoose", { defaultValue: "Choose image" })}
          </button>
          {logo && (
            <button className="asf-btn" disabled={disabled} style={{ marginInlineStart: 8 }} onClick={onRemove}>
              {t("delivery.brandLogoRemove", { defaultValue: "Remove" })}
            </button>
          )}
          <p style={{ fontSize: 11.5, color: "#64748b", margin: "8px 0 0", maxWidth: 320 }}>
            {t("delivery.brandLogoHint", {
              kb: maxKb,
              defaultValue: "PNG, JPEG, GIF or WebP, up to {{kb}} KB. SVG is not accepted — it is a document that can carry script, so export a raster copy.",
            })}
          </p>
          {error && <p style={{ fontSize: 12, color: "#b91c1c", margin: "6px 0 0" }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}

/* ── One colour ────────────────────────────────────────────────────────────────────────
   A swatch and a hex box, kept in step. The native picker cannot express "unset", so the
   text box is the one that owns the value and Clear is a button of its own. */
function ColorField({ t, label, hint, value, fallback, invalid, disabled, onChange }) {
  const valid = /^#[0-9a-f]{6}$/i.test(value || "");
  return (
    <div style={{ marginBottom: 16 }}>
      <label className="asf-lbl">{label}</label>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input type="color" disabled={disabled} value={valid ? value : fallback}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          style={{ width: 44, height: 38, padding: 2, borderRadius: 10, border: "1px solid #e2e8f0",
            background: "#fff", cursor: disabled ? "not-allowed" : "pointer" }} />
        <input className="asf-input" value={value} maxLength={7} placeholder={fallback}
          disabled={disabled} spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 130, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            ...(invalid ? { borderColor: "#fca5a5" } : null) }} />
        {value && (
          <button className="asf-btn" disabled={disabled} onClick={() => onChange("")}
            style={{ padding: "8px 14px", fontSize: 13 }}>
            {t("delivery.brandClear", { defaultValue: "Clear" })}
          </button>
        )}
      </div>
      <p style={{ fontSize: 11.5, color: invalid ? "#b91c1c" : "#64748b", margin: "6px 0 0" }}>
        {invalid ? t("delivery.brandColourInvalid", { defaultValue: "Use a hex colour such as #1b4f9c." }) : hint}
      </p>
    </div>
  );
}

/* ── Preview ───────────────────────────────────────────────────────────────────────────
   The sign-in screen, because it is the one every customer sees and the one where a bad
   colour pairing shows first. Painted entirely from the palette the server resolved, so
   what is on this card is what will be on their screen. */
function BrandPreview({ t, palette, active, logo, name }) {
  const p = active && palette?.branded ? palette : {};
  const accent = p.accent || NEUTRAL.accent;
  const accentInk = p.accentInk || NEUTRAL.accentInk;
  const header = p.header || NEUTRAL.header;
  const headerInk = p.headerInk || NEUTRAL.headerInk;
  const page = p.page || NEUTRAL.page;
  const line = p.accentLine || NEUTRAL.accentLine;

  return (
    <div style={{ flex: "1 1 360px", minWidth: 300, position: "sticky", top: 16 }}>
      <div className="asf-card" style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("delivery.brandPreview", { defaultValue: "Preview" })}</h3>
          {!active && (
            <span style={{ fontSize: 11.5, color: "#64748b" }}>
              {t("delivery.brandNeutral", { defaultValue: "Branding off — the neutral portal" })}
            </span>
          )}
        </div>

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden", background: page }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 14px",
            background: header, borderBottom: "1px solid #e6eaf1" }}>
            {active && logo && <img src={logo} alt="" style={{ height: 22, maxWidth: 130, objectFit: "contain" }} />}
            <span style={{ fontWeight: 700, fontSize: 13, color: headerInk, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {(active && name) || t("delivery.brandFallbackName", { defaultValue: "Document portal" })}
            </span>
            <span style={{ marginInlineStart: "auto", fontSize: 10.5, color: headerInk, opacity: 0.6 }}>English</span>
          </div>

          {/* Sign-in card */}
          <div style={{ padding: "22px 16px" }}>
            <div style={{ background: "#fff", border: "1px solid #e6eaf1", borderRadius: 12, padding: 16,
              maxWidth: 280, margin: "0 auto", boxShadow: "0 10px 30px -18px rgba(15,23,42,.3)" }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                {t("delivery.brandSampleTitle", { defaultValue: "Sign in" })}
              </div>
              <div style={{ fontSize: 11.5, color: "#64748b", margin: "4px 0 14px" }}>
                {t("delivery.brandSampleLead", { defaultValue: "Enter the access code you were given." })}
              </div>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
                color: "#64748b", marginBottom: 5 }}>
                {t("delivery.brandSampleField", { defaultValue: "Access code" })}
              </div>
              <div style={{ border: `1px solid ${accent}`, borderRadius: 8, height: 32, marginBottom: 12,
                boxShadow: `0 0 0 3px ${p.accentRing || "rgba(67,56,202,.14)"}` }} />
              <div style={{ background: accent, color: accentInk, borderRadius: 8, padding: "9px 0",
                textAlign: "center", fontWeight: 600, fontSize: 12.5 }}>
                {t("delivery.brandSampleBtn", { defaultValue: "Continue" })}
              </div>
            </div>

            {/* Tab pill and empty ring, so the brand tints are visible too */}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: "5px 11px", borderRadius: 999,
                background: p.accentSoft || NEUTRAL.accentSoft, border: `1px solid ${line}`, color: accent }}>
                {t("delivery.brandSampleTab", { defaultValue: "Statements" })}
              </span>
            </div>

            {/* Footer */}
            <div style={{ textAlign: "center", marginTop: 18, fontSize: 10.5, color: "#94a3b8" }}>
              <div>
                {(active && p.footerText) || t("delivery.brandFooterPlaceholder", {
                  defaultValue: "Documents are provided by the institution that sent you this link.",
                })}
              </div>
              {active && (p.supportUrl || p.privacyUrl) && (
                <div style={{ marginTop: 5, color: accent, fontWeight: 600 }}>
                  {[p.supportUrl && t("delivery.brandSampleHelp", { defaultValue: "Help" }),
                    p.privacyUrl && t("delivery.brandSamplePrivacy", { defaultValue: "Privacy" })]
                    .filter(Boolean).join("   |   ")}
                </div>
              )}
            </div>
          </div>
        </div>

        <p style={{ fontSize: 11.5, color: "#64748b", margin: "12px 0 0" }}>
          {t("delivery.brandPreviewHint", {
            defaultValue: "Your customers see this in their own language. The colours and the mark are the same for everyone.",
          })}
        </p>
      </div>
    </div>
  );
}
